import { useCallback, useState } from 'react';
import { continuationStep, type ConnectionStep, type CreateAccountResponse, type Schedule } from '@accounts';
import { trackEvent, markFunnelStage } from '@/shared/analytics';
export type { ConnectionStep } from '@accounts';

interface UseScheduleConnectionOptions {
  onCompleted?: () => void;
  onDone?: () => void;
}

export interface UseScheduleConnectionReturn {
  step: ConnectionStep | null;
  connected: (response: CreateAccountResponse) => void;
  chooseSchedule: (schedule: Schedule) => void;
  schedulesFound: (schedules: Schedule[]) => void;
  cancelActivation: () => void;
  complete: () => void;
  reset: () => void;
}

export function useScheduleConnection({
  onCompleted,
  onDone,
}: UseScheduleConnectionOptions = {}): UseScheduleConnectionReturn {
  const [step, setStep] = useState<ConnectionStep | null>(null);

  const connected = useCallback(({ account, schedules }: CreateAccountResponse) => {
    trackEvent('schedules_found', {
      count: schedules.length,
      bookable_count: schedules.filter((schedule) => schedule.bookable).length,
    });
    setStep(continuationStep(account, schedules));
  }, []);

  const schedulesFound = useCallback((schedules: Schedule[]) => {
    setStep((current) =>
      current?.name === 'noSchedules' || current?.name === 'noBookableSchedules'
        ? continuationStep(current.account, schedules)
        : current
    );
  }, []);

  const chooseSchedule = useCallback((schedule: Schedule) => {
    setStep((current) =>
      current?.name === 'pickSchedule'
        ? { name: 'activating', account: current.account, schedule, schedules: current.schedules }
        : current
    );
    trackEvent('schedule_chosen', { schedule_id: schedule.id });
  }, []);

  const cancelActivation = useCallback(() => {
    if (step?.name !== 'activating') return;

    if (step.schedules.length > 1) {
      setStep({ name: 'pickSchedule', account: step.account, schedules: step.schedules });
      return;
    }

    setStep(null);
    onDone?.();
  }, [step, onDone]);

  const complete = useCallback(() => {
    markFunnelStage('completed', 'preparation_completed');
    setStep(null);
    onCompleted?.();
    onDone?.();
  }, [onCompleted, onDone]);

  const reset = useCallback(() => {
    setStep(null);
  }, []);

  return { step, connected, chooseSchedule, schedulesFound, cancelActivation, complete, reset };
}
